import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { usePostHog } from '@posthog/react';
import { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ChatIcon from '@mui/icons-material/Chat';
import { Crisp } from 'crisp-sdk-web';
import { toggleChat } from '@/utils/crisp-chat';
import { matchPath } from 'react-router-dom';
import { appRoutes } from '@/AppRoutes';

export default function ErrorBoundary() {
    const { t } = useTranslation();
    const posthog = usePostHog();
    const [copied, setCopied] = useState(false);

    const publicPaths = appRoutes.filter((r) => r.type === 'public').map((r) => r.path);
    const isPublicRoute = publicPaths.some((path) => matchPath(path, window.location.pathname));

    const sessionId = posthog?.get_session_id() || 'N/A';

    const handleCopySessionId = async () => {
        try {
            await navigator.clipboard.writeText(sessionId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error(`Failed to copy session ID "${sessionId}":`, err);
        }
    };

    const supportMessage = t('error.page.something-wrong.support-message', { sessionId });

    const mailtoLink = `mailto:sayhello@swonkie.com?subject=${encodeURIComponent(t('error.page.something-wrong.title'))}&body=${encodeURIComponent(supportMessage)}`;

    const handleOpenSupportChat = () => {
        try {
            toggleChat(true, 'chat');
            Crisp.message.setMessageText(supportMessage);
        } catch (error) {
            console.error('Failed to open Crisp chat, falling back to mailto:', error);
            // Fallback to mailto if Crisp is not available
            window.location.href = mailtoLink;
        }
    };

    return (
        <Stack height="100vh" alignItems="center" justifyContent="center" flex={1} p={3}>
            <Stack
                direction="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                maxWidth={924}
            >
                <Box component="img" src="/error.svg" alt="" height={256} width={256} />
                <Typography variant="h2" color="text.primary" mt={2}>
                    {t('error.page.something-wrong.title')}
                </Typography>
                <Typography variant="h5" color="text.primary" mt={2}>
                    {t('error.page.something-wrong.subtitle')}
                </Typography>

                {/* Session ID Section */}
                <Box
                    sx={{
                        mt: 4,
                        p: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                        {t('error.page.something-wrong.session-id.label')}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                            fontFamily: 'monospace',
                            bgcolor: 'action.hover',
                            px: 1,
                            py: 0.5,
                            borderRadius: 0.5,
                            flex: 1,
                        }}
                    >
                        {sessionId}
                    </Typography>
                    <Tooltip
                        title={
                            copied
                                ? t('error.page.something-wrong.session-id.tooltip.copied')
                                : t('error.page.something-wrong.session-id.tooltip.copy')
                        }
                    >
                        <IconButton size="small" onClick={handleCopySessionId} color="primary">
                            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Box>

                <Stack direction="row" spacing={2} sx={{ mt: 4, mb: 3 }}>
                    {isPublicRoute ? (
                        <Button size="large" variant="contained" onClick={() => window.location.reload()}>
                            {t('error.page.something-wrong.action.button.refresh')}
                        </Button>
                    ) : (
                        <Button size="large" variant="contained" href={'/'}>
                            {t('error.page.something-wrong.action.button.go-home')}
                        </Button>
                    )}
                    <Button
                        size="large"
                        variant="outlined"
                        startIcon={<ChatIcon />}
                        onClick={handleOpenSupportChat}
                    >
                        {t('error.page.something-wrong.action.button.open-support')}
                    </Button>
                </Stack>

                <Typography variant="caption" color="text.secondary">
                    <Trans
                        i18nKey={'error.page.something-wrong.helper-text'}
                        components={{
                            email: (
                                <Typography
                                    component="a"
                                    href={mailtoLink}
                                    variant="caption"
                                    color="primary.main"
                                    display="inline-block"
                                    sx={{ textDecoration: 'none' }}
                                />
                            ),
                        }}
                    />
                </Typography>
            </Stack>
        </Stack>
    );
}
